import * as Sentry from '@sentry/react';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import {
  appDataInitialValues,
  documentVisibleAtom,
} from '@listening/frontend/query/api-runtime';
import { Layer } from 'effect';
import { Loader, WaterfallDetection } from 'raf-effect-atom';
import {
  makeRegistry,
  setupRouterAtoms,
} from 'raf-effect-atom/tanstack-router';
import { Error404 } from './components/astro/404';
import { LoadingComponent } from './components/shared-ui/loading-component';
import { DefaultCatchBoundary } from './components/start/DefaultCatchBoundary';
import { deLocalizeUrl, localizeUrl } from '@/paraglide/runtime';
import { routeTree } from './routeTree.gen';
// Stale-chunk recovery: the import registers the `vite:preloadError` listener
// (side effect) and exposes the two halves of `reportRouteError` below —
// the reload attempt, and the predicate that says whether the resulting
// failure is recovery noise. See stale-chunk.ts.
import {
  handleRouterStaleChunk,
  shouldSuppressStaleChunkError,
} from './stale-chunk';
import { parseSearch, stringifySearch } from './lib/search-params';
import { apiTransport } from '@listening/api-contract/api-client-tag';
import { getApiTransportOptions, getRuntime } from './runtimes/get-runtime';
import { setServerReadableKvsStorage } from '@listening/frontend/atoms/kvs';
import { librarySortModeKey } from '@listening/frontend/stores/library-sort-store';
import { cookieKvsStorage } from './lib/cookie-kvs-storage';

// The server renders the library in the user's order, so it reads the sort
// from the page request.
setServerReadableKvsStorage(librarySortModeKey, cookieKvsStorage);

// Every error that reaches `DefaultCatchBoundary` — the user-visible
// "Something went wrong!" screen — for both render and loader failures.
// TanStack strips `notFound` and redirect errors before calling this, so a
// 404 is never reported as an exception. Attempting the stale-chunk reload
// first is what makes the suppression correct: a reload that was scheduled
// is navigating away and its chunk error is recovery noise, while one the
// rate limit refused leaves the user stuck on the boundary and must report.
function reportRouteError(error: unknown): void {
  if (error instanceof Error) handleRouterStaleChunk(error);
  if (shouldSuppressStaleChunkError(error)) return;
  Sentry.captureException(error);
}

export function getRouter() {
  // One registry per SSR request and one per tab. raf-effect-atom streams the
  // server's into the browser's, results still loading included, and disposes
  // the server's when the response finishes.
  const registry = makeRegistry({
    initialValues: appDataInitialValues({
      // The server runtime reads the request's headers when a call runs, so
      // one runtime serves every SSR request.
      layer: Layer.effectContext(getRuntime().contextEffect),
      visible: documentVisibleAtom,
      api: apiTransport(getApiTransportOptions()),
    }),
  });

  const router = createTanStackRouter({
    routeTree,
    context: {
      atoms: Loader.make(registry),
      registry,
      cloudfrontRegion: undefined,
      isFreePlanCountry: undefined,
      analyticsBootstrap: undefined,
      edgeFlags: undefined,
      viewport: undefined,
    },
    defaultPreload: 'intent',
    defaultPendingComponent: LoadingComponent,
    defaultPendingMinMs: 0,
    notFoundMode: 'fuzzy',
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <Error404 />,
    rewrite: {
      input: ({ url }) => deLocalizeUrl(url),
      output: ({ url }) => localizeUrl(url),
    },
    defaultOnCatch: reportRouteError,
    // In development, a read of an atom no loader started throws to the
    // nearest error boundary, naming the read.
    Wrap: ({ children }) => (
      <WaterfallDetection mode='throw'>{children}</WaterfallDetection>
    ),
    parseSearch,
    stringifySearch,
  });
  setupRouterAtoms({ router, registry });

  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
